import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAuth } from '../../hooks/useAuth';
import { useTutorialProgress } from '../../hooks/useTutorialProgress';
import { tutorialVideos, getVideoUrl } from '../../config/tutorialVideos';
import type { TutorialVideo } from '../../config/tutorialVideos';
import './AcademyPage.css';

const isMobileDevice = () => window.innerWidth < 768;

function formatRelativeDate(isoString: string | null | undefined): string | null {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function VideoDuration({ src }: { src: string }) {
  const [duration, setDuration] = useState<string | null>(null);

  useEffect(() => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = src;
    video.onloadedmetadata = () => {
      const secs = Math.floor(video.duration);
      const m = Math.floor(secs / 60);
      const s = String(secs % 60).padStart(2, '0');
      setDuration(`${m}:${s}`);
    };
  }, [src]);

  if (!duration) return null;
  return <span className="academy-video-duration">{duration}</span>;
}

function VideoCard({
  video,
  isSelected,
  isWatched,
  lastWatchedDate,
  onSelect,
}: {
  video: TutorialVideo;
  isSelected: boolean;
  isWatched: boolean;
  lastWatchedDate: string | null;
  onSelect: (video: TutorialVideo) => void;
}) {
  return (
    <button
      className={`academy-video-card ${isSelected ? 'is-selected' : ''}`}
      onClick={() => onSelect(video)}
      aria-label={`Play ${video.title}`}
    >
      <div className="academy-card-play-icon">
        <span className="material-symbols-outlined">play_circle</span>
      </div>
      <div className="academy-card-body">
        <div className="academy-card-header-row">
          <span className="academy-card-title">{video.title}</span>
          {isWatched ? (
            <span className="academy-watched-badge">
              <span className="material-symbols-outlined">check_circle</span>
            </span>
          ) : (
            <span className="academy-new-badge">NEW</span>
          )}
        </div>
        <p className="academy-card-desc">{video.description}</p>
        <div className="academy-card-footer">
          <VideoDuration src={getVideoUrl(video.filename)} />
          {isWatched && lastWatchedDate && (
            <span className="academy-last-watched">{formatRelativeDate(lastWatchedDate)}</span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function AcademyPage() {
  const navigate = useNavigate();
  const { isCustomer } = useAdmin();
  const { user } = useAuth();
  const userEmail = user?.email || null;
  const { progress, markWatched } = useTutorialProgress(userEmail);

  const [selectedVideo, setSelectedVideo] = useState<TutorialVideo>(tutorialVideos[0]);
  const [isMobile, setIsMobile] = useState(isMobileDevice);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVideoSelect = useCallback((video: TutorialVideo) => {
    if (isMobile) {
      const videoEl = document.createElement('video');
      videoEl.src = getVideoUrl(video.filename);
      videoEl.controls = true;
      videoEl.autoplay = true;

      const handlePlay = () => {
        videoEl.requestFullscreen?.().then(() => {
          screen.orientation?.lock('landscape').catch(() => {});
        }).catch(() => {});
        markWatched(video.id);
      };

      videoEl.addEventListener('play', handlePlay, { once: true });

      document.body.appendChild(videoEl);
      videoEl.play().catch(() => {});

      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          document.body.removeChild(videoEl);
          document.removeEventListener('fullscreenchange', handleFullscreenChange);
        }
      };
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return;
    }

    setSelectedVideo(video);
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [isMobile, markWatched]);

  const handleVideoPlaying = useCallback(() => {
    if (selectedVideo) {
      markWatched(selectedVideo.id);
    }
  }, [selectedVideo, markWatched]);

  return (
    <div className="academy-page">
      <header className="academy-header">
        <div className="academy-logo">
          <img src="/static/ManageConnect-Logo.svg" alt="ManageConnect Logo" className="academy-logo-img" onClick={() => navigate(isCustomer ? '/customer' : '/')} />
          <span>Training Videos</span>
        </div>
      </header>

      <div className="academy-body">
        <aside className="academy-sidebar">
          <h2 className="academy-sidebar-title">Tutorial Videos</h2>
          <div className="academy-card-list">
            {tutorialVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                isSelected={!isMobile && selectedVideo?.id === video.id}
                isWatched={!!progress[video.id]}
                lastWatchedDate={progress[video.id] || null}
                onSelect={handleVideoSelect}
              />
            ))}
          </div>
        </aside>

        {!isMobile && selectedVideo && (
          <main className="academy-player-area">
            <div className="academy-player-wrapper">
              <video
                ref={videoRef}
                key={selectedVideo.id}
                className="academy-video"
                controls
                playsInline
                src={getVideoUrl(selectedVideo.filename)}
                onPlaying={handleVideoPlaying}
              />
            </div>
            <div className="academy-player-meta">
              <h1 className="academy-player-title">{selectedVideo.title}</h1>
              <p className="academy-player-desc">{selectedVideo.description}</p>
              {progress[selectedVideo.id] && (
                <span className="academy-player-watched">
                  <span className="material-symbols-outlined">check_circle</span>
                  Last watched {formatRelativeDate(progress[selectedVideo.id])}
                </span>
              )}
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
